import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { useMemo } from 'react';
import useVaultProgress from '@modules/vault/hooks/queries/useVaultProgress';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { APPLICANT_TYPE, MORTGAGE_USAGE } from '@modules/opportunities/entities/opportunity/const';
import {
  CaseCreationBasicInfoRoute,
  CaseCreationBankDocumentsRoute,
  CaseCreationBankFormsRoute,
  CaseCreationSubmitApplicationsRoute,
  CaseCreationSubmissionRootRoute,
  CaseCreationFillFormsRoute,
  CaseCreationSelectBankProductsRoute,
  CaseCreationVaultFillFormsRoute,
  CaseCreationBanksFillFormsRoute
} from '@modules/opportunities/presentation/routes';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { CASE_CREATION_RULES } from '@modules/opportunities/presentation/pages/CaseCreation/rules';
import { mapOpportunityDataToCaseBasicInfoFormES, mapOpportunityDataToCaseBasicInfoFormUAE } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/mappers';
import { caseBasicInfoSchemaUAE } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/validators/ae';
import { caseBasicInfoSchemaES } from '@modules/opportunities/presentation/pages/CaseCreation/views/CaseBasicInfo/validators/es';
import { CaseCreationEngine } from '@modules/opportunities/presentation/pages/CaseCreation/hooks/useCaseCreationStateMachineEngine';

const calculateBasicInfoCompletedStepsNumber = (opportunity?: Opportunity, hasAdditionalApplicant?: boolean) => {
  if (opportunity) {
    const schemas = {
      [BUSINESS_IDENTIFIER.AE_HUSPY]: caseBasicInfoSchemaUAE.safeParse(
        mapOpportunityDataToCaseBasicInfoFormUAE(opportunity)
      ),
      [BUSINESS_IDENTIFIER.ES_BAYTECA]: caseBasicInfoSchemaES.safeParse(
        mapOpportunityDataToCaseBasicInfoFormES(opportunity as unknown as Opportunity<'ES'>)
      ),
    };
    const err = schemas[APPLICATION_BUSINESS_IDENTIFIER];
    let numberOfCompletedFields = CASE_CREATION_RULES.BASIC_INFO_REQUIRED_NUMBER_OF_FIELDS(hasAdditionalApplicant);
    if (!err.success) {
      numberOfCompletedFields -= err.error.errors.length;
    }

    return numberOfCompletedFields;
  }

  return 0;
};

const useComputeCaseCreationState = ({ opportunity, vaultProgress, hasSelectedBanks }: {
  opportunity?: Opportunity,
  vaultProgress?: ReturnType<typeof useVaultProgress>['data'],
  hasSelectedBanks: boolean,
}) => {
  const hasAdditionalApplicant = opportunity?.applicants.some(
    (item) => item.applicant_type !== APPLICANT_TYPE.mainApplicant
  );

  const isCommercial = opportunity?.mortgage_usage === MORTGAGE_USAGE.COMMERCIAL;

  const STATE_MACHINE = useMemo(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (): CaseCreationEngine.StateMachine => {
      const basicInfoProgress = calculateBasicInfoCompletedStepsNumber(opportunity, hasAdditionalApplicant);
      const basicInfoTotalSteps = CASE_CREATION_RULES.BASIC_INFO_REQUIRED_NUMBER_OF_FIELDS(hasAdditionalApplicant);
      const isBasicInfoCompleted = hasSelectedBanks && basicInfoProgress === basicInfoTotalSteps;
      const isBankProductsStepCompleted = IS_SPAIN_ENV || CASE_CREATION_RULES.BANK_SUBMISSION.BANK_PRODUCTS_COMPLETION(
        opportunity?.bank_applications.map((bankApp) => !!bankApp.bank_details.selected_rate.is_default_rate) ?? []
      );
      const isBankFormsStepCompleted = CASE_CREATION_RULES.BANK_SUBMISSION.BANK_FORMS_COMPLETION(
        opportunity?.bank_applications.map((bankApp) => {
          if (bankApp.status !== BANK_APPLICATION_STATUS.draft) {
            return true;
          }
          return bankApp.documents.length > 0;
        }) ?? []
      );

      const hasAccessToUploadBankDocuments = (isCommercial && isBasicInfoCompleted)
       || (isBasicInfoCompleted && isBankProductsStepCompleted);

      const hasAccessToSubmitBank = (isCommercial && isBasicInfoCompleted)
        || (isBasicInfoCompleted
          && isBankFormsStepCompleted
          && isBankProductsStepCompleted);

      return {
        [CaseCreationBasicInfoRoute.to]: {
          isAccessible: true,
          isComplete: basicInfoProgress === basicInfoTotalSteps,
          stateMetadata: {
            completedSteps: basicInfoProgress,
            totalSteps: basicInfoTotalSteps,
          },
        },
        [CaseCreationBankDocumentsRoute.to]: {
          isAccessible: hasSelectedBanks,
          isComplete: CASE_CREATION_RULES.BANK_DOCUMENTS_COMPLETION(
            vaultProgress?.documentProgress?.uploaded,
            vaultProgress?.documentProgress?.total
          ),
          stateMetadata: {
            completedSteps: vaultProgress?.documentProgress?.uploaded!,
            totalSteps: vaultProgress?.documentProgress?.total!,
          },
        },
        [CaseCreationFillFormsRoute.to]: {
          isAccessible: isCommercial ? false : isBasicInfoCompleted,
          isComplete: false,
        },
        [CaseCreationVaultFillFormsRoute.to]: {
          isAccessible: isCommercial ? false : isBasicInfoCompleted,
          isComplete: false,
          isSkipped: isCommercial,
        },
        [CaseCreationBanksFillFormsRoute.to]: {
          isAccessible: isCommercial ? false : isBasicInfoCompleted,
          isComplete: false,
          isSkipped: IS_SPAIN_ENV || isCommercial,
        },
        [CaseCreationSubmissionRootRoute.to]: {
          isAccessible: isBasicInfoCompleted,
          isComplete: false,
        },
        [CaseCreationSelectBankProductsRoute.to]: {
          isAccessible: isCommercial ? false : isBasicInfoCompleted,
          isComplete: isBankProductsStepCompleted,
          isSkipped: isCommercial,
        },
        [CaseCreationBankFormsRoute.to]: {
          isAccessible: hasAccessToUploadBankDocuments,
          isComplete: isBankFormsStepCompleted && isBankProductsStepCompleted,
        },
        [CaseCreationSubmitApplicationsRoute.to]: {
          isAccessible: hasAccessToSubmitBank,
          isComplete: false,
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [opportunity, vaultProgress?.documentProgress]
  );

  return { STATE_MACHINE };
};

export default useComputeCaseCreationState;
